import { useEffect, useState } from "react";
import {
  BrowserRouter,
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

import { AuthProvider, useAuth } from "./context/AuthContext";
import { EditionProvider, useEdition } from "./context/EditionContext";
import {
  FinancialYearProvider,
  useFinancialYear,
} from "./context/FinancialYearContext";
import ProtectedRoute from "./components/ProtectedRoute";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import UnauthorizedPage from "./pages/UnauthorizedPage";
import AccountsPage from "./pages/AccountsPage";
import TrialBalancePage from "./pages/TrialBalancePage";
import ProfitLossPage from "./pages/ProfitLossPage";
import BalanceSheetPage from "./pages/BalanceSheetPage";
import ItemsPage from "./pages/ItemsPage";
import StockLedgerPage from "./pages/StockLedgerPage";
import EmployeesPage from "./pages/EmployeesPage";
import PayrollPage from "./pages/PayrollPage";
import CustomersPage from "./pages/CustomersPage";
import InvoicesPage from "./pages/InvoicesPage";
import JournalPage from "./pages/JournalPage";
import LedgerPage from "./pages/LedgerPage";
import FinalAccountsPage from "./pages/FinalAccountsPage";
import PurchaseVoucherPage from "./pages/PurchaseVoucherPage";
import CreditNotePage from "./pages/CreditNotePage";
import DebitNotePage from "./pages/DebitNotePage";
import ReceiptVoucherPage from "./pages/ReceiptVoucherPage";
import PaymentVoucherPage from "./pages/PaymentVoucherPage";
import SalesVoucherPage from "./pages/SalesVoucherPage";
import SalesReturnPage from "./pages/SalesReturnPage";
import InvoicePrintPage from "./pages/InvoicePrintPage";
import InvoiceTemplateListPage from "./pages/InvoiceTemplateListPage";
import InvoiceTemplateDesignerPage from "./pages/InvoiceTemplateDesignerPage";
import InvoiceTemplatePrintPage from "./pages/InvoiceTemplatePrintPage";
import GstReportsPage from "./pages/GstReportsPage";
import GstEnhancementsPage from "./pages/GstEnhancementsPage";
import GstFinalReportsPage from "./pages/GstFinalReportsPage";
import EWayBillPage from "./pages/EWayBillPage";
import CashBookPage from "./pages/CashBookPage";
import ContraVoucherPage from "./pages/ContraVoucherPage";
import DayBookPage from "./pages/DayBookPage";
import CustomerOutstandingPage from "./pages/CustomerOutstandingPage";
import SupplierOutstandingPage from "./pages/SupplierOutstandingPage";
import BankReconciliationPage from "./pages/BankReconciliationPage";
import OpeningBalancePage from "./pages/OpeningBalancePage";
import BankBookPage from "./pages/BankBookPage";
import CompanySetupPage from "./pages/CompanySetupPage";
import PermissionsPage from "./pages/PermissionsPage";
import LicensePage from "./pages/LicensePage";
import StockAdjustmentPage from "./pages/StockAdjustmentPage";
import StockSummaryPage from "./pages/StockSummaryPage";
import SuppliersPage from "./pages/SuppliersPage";
import ErrorBoundary from "./components/ErrorBoundary";
import ApiHealthBadge from "./components/ApiHealthBadge";
import GlobalLoadingOverlay from "./components/GlobalLoadingOverlay";
import AuditLogsPage from "./pages/AuditLogsPage";
import TransactionLogsPage from "./pages/TransactionLogsPage";
import DeletedEntriesPage from "./pages/DeletedEntriesPage";
import UserActivityPage from "./pages/UserActivityPage";
import GstVoucherPage from "./pages/GSTVoucherPage";
import EditManagerPage from "./pages/EditManagerPage";
import YearClosingPage from "./pages/YearClosingPage";
import CompanyReportsDashboardPage from "./pages/CompanyReportsDashboardPage";
import SystemSettingsPage from "./pages/SystemSettingsPage";
import EditionSettingsPage from "./pages/EditionSettingsPage";
import quantaLogo from "./assets/quanta.png";

const uiTheme = {
  sidebar: "linear-gradient(180deg,#07111f 0%,#102a43 45%,#0f766e 100%)",
  sidebarSoft: "rgba(255,255,255,.10)",
  sidebarHover: "rgba(255,255,255,.16)",
  primary: "#0f766e",
  primaryDark: "#0b4f4a",
  accent: "#f59e0b",
  pageBg: "#eef4f8",
  card: "#ffffff",
  text: "#0f172a",
  muted: "#64748b",
};

function useKeyboardShortcuts() {
  const navigate = useNavigate();
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
      const routes = {
        s: "/sales-voucher",
        p: "/purchase-voucher",
        j: "/journal",
        l: "/ledger",
        t: "/trial-balance",
        r: "/profit-loss",
        b: "/balance-sheet",
        i: "/invoice-templates",
        g: "/gst-reports",
        a: "/audit-logs",
        c: "/cash-book",
      };
      const key = e.key.toLowerCase();
      if (e.altKey && routes[key]) {
        e.preventDefault();
        navigate(routes[key]);
      }
      if (e.altKey && key === "k")
        alert(
          "Shortcuts:\nAlt+S Sales, Alt+P Purchase, Alt+J Journal, Alt+L Ledger, Alt+C Cash Book, Alt+T Trial Balance, Alt+B Balance Sheet, Alt+G GST",
        );
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);
}

const menuSections = [
  {
    key: "finance",
    title: "Finance",
    icon: "bi-bank",
    items: [
      ["/dashboard", "Company Reports", "bi-speedometer2", "REPORT_VIEW"],
      ["/accounts", "Accounts", "bi-wallet2", "ACCOUNT_VIEW"],
      ["/journal", "Journal", "bi-journal-text", "JOURNAL_SAVE"],
      [
        "/edit-manager",
        "Edit/Delete Manager",
        "bi-pencil-square",
        "JOURNAL_SAVE",
      ],
      ["/ledger", "Ledger", "bi-book", "REPORT_VIEW"],
      [
        "/opening-balance",
        "Opening Balance",
        "bi-arrow-up-circle",
        "JOURNAL_SAVE",
      ],
      ["/cash-book", "Cash Book", "bi-cash-stack", "REPORT_VIEW"],
      ["/bank-book", "Bank Book", "bi-bank", "REPORT_VIEW"],
      [
        "/bank-reconciliation",
        "Bank Reconciliation",
        "bi-check2-square",
        "REPORT_VIEW",
      ],
      ["/trial-balance", "Trial Balance", "bi-calculator", "REPORT_VIEW"],
      ["/profit-loss", "Profit & Loss", "bi-graph-up-arrow", "REPORT_VIEW"],
      [
        "/balance-sheet",
        "Balance Sheet",
        "bi-file-earmark-bar-graph",
        "REPORT_VIEW",
      ],
      [
        "/final-accounts",
        "Final Accounts",
        "bi-file-earmark-check",
        "REPORT_VIEW",
      ],
      ["/year-closing", "Year Closing", "bi-calendar-check", "YEAR_CLOSE"],
      [
        "/contra-voucher",
        "Contra Voucher",
        "bi-arrow-left-right",
        "JOURNAL_SAVE",
      ],
      ["/day-book", "Day Book", "bi-journal-bookmark", "REPORT_VIEW"],
      [
        "/customer-outstanding",
        "Receivables",
        "bi-person-check",
        "REPORT_VIEW",
      ],
      ["/supplier-outstanding", "Payables", "bi-person-dash", "REPORT_VIEW"],
    ],
  },
  {
    key: "inventory",
    title: "Inventory",
    icon: "bi-box-seam",
    items: [
      ["/items", "Items", "bi-box", "ITEM_VIEW"],
      ["/stock-ledger", "Stock Ledger", "bi-archive", "STOCK_LEDGER_VIEW"],
      ["/stock-summary", "Stock Summary", "bi-boxes", "STOCK_LEDGER_VIEW"],
      [
        "/stock-adjustment",
        "Stock Adjustment",
        "bi-plus-slash-minus",
        "STOCK_ADJUST",
      ],
    ],
  },
  {
    key: "hr",
    title: "HR",
    icon: "bi-people",
    items: [
      ["/employees", "Employees", "bi-people-fill", "USER_SAVE"],
      ["/payroll", "Payroll", "bi-cash-stack", "USER_SAVE"],
    ],
  },
  {
    key: "sales",
    title: "Sales & Vouchers",
    icon: "bi-receipt",
    items: [
      ["/customers", "Customers", "bi-person-vcard", "SALES_SAVE"],
      ["/suppliers", "Suppliers", "bi-truck", "PURCHASE_SAVE"],
      ["/invoices", "Invoices", "bi-receipt-cutoff", "SALES_SAVE"],
      ["/sales-voucher", "Sales Voucher", "bi-cart", "SALES_SAVE"],
      ["/sales-return", "Sales Return", "bi-arrow-return-left", "SALES_SAVE"],
      [
        "/purchase-voucher",
        "Purchase Voucher",
        "bi-cart-plus",
        "PURCHASE_SAVE",
      ],
      ["/credit-note", "Credit Note", "bi-arrow-return-left", "SALES_SAVE"],
      ["/debit-note", "Debit Note", "bi-arrow-return-right", "PURCHASE_SAVE"],
      ["/receipt-voucher", "Receipt Voucher", "bi-wallet", "JOURNAL_SAVE"],
      ["/payment-voucher", "Payment Voucher", "bi-credit-card", "JOURNAL_SAVE"],
      [
        "/gst-reports",
        "GST Reports",
        "bi-file-earmark-spreadsheet",
        "GST_REPORT_VIEW",
      ],
      ["/eway-bill", "E-Way Bill", "bi-truck", "GST_REPORT_VIEW"],
      ["/gst-final-reports", "GST Final Reports", "bi-file-earmark-spreadsheet", "GST_REPORT_VIEW"],
      ["/gst-voucher", "GST Voucher", "bi-receipt", "GST_VOUCHER_SAVE"],
      ["/gst-enhancements", "GST Enhancements", "bi-receipt-cutoff", "GST_REPORT_VIEW"],
    ],
  },
  {
    key: "admin",
    title: "Admin & Security",
    icon: "bi-shield-lock",
    items: [
      ["/companies", "Companies", "bi-buildings", "COMPANY_VIEW"],
      ["/admin-users", "Register Users", "bi-person-plus", "USER_SAVE"],
      [
        "/permissions",
        "Roles & Permissions",
        "bi-person-gear",
        "PERMISSION_SAVE",
      ],
      ["/license", "License Keys", "bi-key", "LICENSE_GENERATE"],
      ["/audit-logs", "Audit Logs", "bi-clipboard-data", "AUDIT_VIEW"],
      ["/transaction-logs", "Transaction Logs", "bi-activity", "AUDIT_VIEW"],
      ["/deleted-entries", "Deleted Entries", "bi-trash3", "AUDIT_VIEW"],
      ["/user-activity", "User Activity", "bi-person-lines-fill", "AUDIT_VIEW"],
      ["/system-settings", "System Settings", "bi-gear-wide-connected", "PERMISSION_SAVE"],
    ],
  },
  {
    key: "templates",
    title: "Templates",
    icon: "bi-brush",
    items: [
      [
        "/invoice-templates",
        "Invoice Templates",
        "bi-layout-text-window-reverse",
        "SALES_SAVE",
      ],
    ],
  },
];

const routeMetaMap = (() => {
  const map = new Map();
  menuSections.forEach((section) => {
    section.items.forEach(([to, label, icon]) => {
      map.set(to, { label, icon, section: section.title });
    });
  });
  map.set("/dashboard", { label: "Company Reports", icon: "bi-speedometer2", section: "Dashboard" });
  map.set("/company-setup", { label: "Company Setup", icon: "bi-buildings", section: "Admin" });
  map.set("/system-settings", { label: "System Settings", icon: "bi-gear-wide-connected", section: "Admin" });
  return map;
})();

function getRouteMeta(pathname) {
  if (routeMetaMap.has(pathname)) return routeMetaMap.get(pathname);
  const matched = [...routeMetaMap.entries()]
    .filter(([route]) => pathname === route || pathname.startsWith(route + "/"))
    .sort((a, b) => b[0].length - a[0].length)[0];

  return matched?.[1] || {
    label: pathname.replace(/^\//, "").replace(/-/g, " ") || "Dashboard",
    icon: "bi-window",
    section: "Workspace",
  };
}

function WorkspaceTabs() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;

  const [tabs, setTabs] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("quantaWorkspaceTabs") || "[]");
      return Array.isArray(saved) && saved.length ? saved : [{ path: "/dashboard", ...getRouteMeta("/dashboard") }];
    } catch {
      return [{ path: "/dashboard", ...getRouteMeta("/dashboard") }];
    }
  });

  useEffect(() => {
    if (["/login", "/register", "/unauthorized"].includes(currentPath)) return;

    setTabs((prev) => {
      const exists = prev.some((t) => t.path === currentPath);
      const next = exists
        ? prev
        : [...prev, { path: currentPath, ...getRouteMeta(currentPath) }].slice(-8);

      localStorage.setItem("quantaWorkspaceTabs", JSON.stringify(next));
      return next;
    });
  }, [currentPath]);

  const closeTab = (e, path) => {
    e.preventDefault();
    e.stopPropagation();

    setTabs((prev) => {
      const next = prev.filter((x) => x.path !== path);
      const safeNext = next.length ? next : [{ path: "/dashboard", ...getRouteMeta("/dashboard") }];
      localStorage.setItem("quantaWorkspaceTabs", JSON.stringify(safeNext));

      if (path === currentPath) {
        const fallback = safeNext[safeNext.length - 1]?.path || "/dashboard";
        navigate(fallback);
      }

      return safeNext;
    });
  };

  if (!tabs.length) return null;

  return (
    <div
      className="quanta-tabs d-flex align-items-center gap-1 mb-2"
      style={{
        overflowX: "auto",
        overflowY: "hidden",
        padding: "6px 8px",
        background: "rgba(255,255,255,.78)",
        border: "1px solid rgba(15,118,110,.12)",
        borderRadius: 14,
        boxShadow: "0 1px 7px rgba(15,23,42,.06)",
        backdropFilter: "blur(8px)",
      }}
    >
      {tabs.map((tab) => {
        const active = currentPath === tab.path || currentPath.startsWith(tab.path + "/");
        return (
          <button
            key={tab.path}
            type="button"
            onClick={() => navigate(tab.path)}
            className="btn btn-sm d-flex align-items-center gap-2 border-0"
            style={{
              height: 32,
              borderRadius: 999,
              whiteSpace: "nowrap",
              fontSize: 11,
              fontWeight: 700,
              padding: "5px 9px",
              color: active ? "#fff" : uiTheme.text,
              background: active
                ? `linear-gradient(90deg,${uiTheme.primaryDark},${uiTheme.primary})`
                : "#f8fafc",
              boxShadow: active ? "0 6px 16px rgba(15,118,110,.24)" : "inset 0 0 0 1px #e2e8f0",
            }}
            title={`${tab.section} / ${tab.label}`}
          >
            <i className={`bi ${tab.icon || "bi-window"}`}></i>
            <span>{tab.label}</span>
            {tabs.length > 1 && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => closeTab(e, tab.path)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") closeTab(e, tab.path);
                }}
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 999,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: active ? "rgba(255,255,255,.18)" : "#e2e8f0",
                  color: active ? "#fff" : "#475569",
                }}
              >
                <i className="bi bi-x" style={{ fontSize: 13 }}></i>
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function Sidebar() {
  const location = useLocation();
  const { hasPermission, isAuthenticated } = useAuth();
  const { canUseRoute, edition } = useEdition();

  const [collapsed, setCollapsed] = useState(false);
  const [openSection, setOpenSection] = useState("finance");

  return (
    <aside
      className="text-white shadow"
      style={{
        width: collapsed ? 70 : 250,
        minWidth: collapsed ? 70 : 250,
        maxWidth: collapsed ? 70 : 250,
        flex: `0 0 ${collapsed ? 70 : 250}px`,
        flexShrink: 0,
        height: "100vh",
        overflowY: "auto",
        overflowX: "hidden",
        transition: "all .25s ease",
        background: uiTheme.sidebar,
        borderRight: "1px solid rgba(255,255,255,.12)",
        boxShadow: "8px 0 24px rgba(15,23,42,.16)",
      }}
    >
      {/* HEADER */}
      <div
        className="px-2 py-1 border-bottom border-light border-opacity-25 d-flex justify-content-between align-items-center"
        style={{
          minHeight: collapsed ? 52 : 68,
        }}
      >
        {!collapsed && (
          <div
            style={{
              flex: 1,
              overflow: "hidden",
              marginTop: -25,
              marginBottom: -25,
              paddingRight: 4,
            }}
          >
            <img
              src={quantaLogo}
              alt="Quanta"
              style={{
                width: "100%",
                maxWidth: 210,
                height: "auto",
                display: "block",
                objectFit: "contain",
                background: "transparent",
              }}
            />
          </div>
        )}

        <button
          className="btn btn-sm text-white border-0 d-flex align-items-center justify-content-center"
          style={{
            flexShrink: 0,
            marginLeft: collapsed ? 0 : 2,
            boxShadow: "none",
            width: collapsed ? 36 : 30,
            height: collapsed ? 36 : 30,
            padding: 0,
          }}
          onClick={() => setCollapsed(!collapsed)}
        >
          <i
            className={`bi ${collapsed ? "bi-list" : "bi-x-lg"}`}
            style={{
              fontSize: collapsed ? 20 : 16,
              lineHeight: 1,
            }}
          ></i>
        </button>
      </div>

      {/* MENU */}
      <div className={collapsed ? "px-1 py-2" : "p-2"}>
        {menuSections.map((sec) => {
          const items = !isAuthenticated
            ? sec.items.filter((x) => ["/system-settings", "/companies"].includes(x[0]))
            : sec.items.filter((x) => hasPermission(x[3]) && canUseRoute(x[0]));

          if (!items.length) return null;

          return (
            <div key={sec.key} className={collapsed ? "mb-1" : "mb-2"}>
              {/* SECTION HEADER */}
              <button
                type="button"
                onClick={() =>
                  setOpenSection(openSection === sec.key ? "" : sec.key)
                }
                className="btn w-100 text-white d-flex align-items-center rounded-3"
                style={{
                  backgroundColor: uiTheme.sidebarSoft,
                  border: "none",
                  fontSize: 12,
                  minHeight: collapsed ? 36 : 34,
                  padding: collapsed ? "6px 0" : "6px 10px",
                  justifyContent: collapsed ? "center" : "space-between",
                }}
              >
                <span
                  className="d-flex align-items-center"
                  style={{
                    gap: collapsed ? 0 : 8,
                  }}
                >
                  <i
                    className={`bi ${sec.icon}`}
                    style={{
                      fontSize: 14,
                    }}
                  ></i>

                  {!collapsed && sec.title}
                </span>

                {!collapsed && (
                  <i
                    className={`bi ${
                      openSection === sec.key
                        ? "bi-chevron-up"
                        : "bi-chevron-down"
                    }`}
                  ></i>
                )}
              </button>

              {/* MENU ITEMS */}
              {openSection === sec.key && (
                <div
                  className="mt-1 d-grid"
                  style={{
                    gap: collapsed ? 4 : 5,
                  }}
                >
                  {items.map(([to, label, icon]) => {
                    const setupTo = !isAuthenticated && to === "/companies" ? "/company-setup" : to;
                    return (
                    <Link
                      key={to}
                      to={setupTo}
                      className={`text-decoration-none rounded-3 d-flex align-items-center ${
                        location.pathname === to ||
                        location.pathname.startsWith(to + "/")
                          ? "fw-semibold"
                          : "text-white"
                      }`}
                      style={{
                        minHeight: collapsed ? 34 : 32,
                        padding: collapsed ? "6px 0" : "6px 10px",
                        marginLeft: collapsed ? 0 : 4,
                        fontSize: 12,
                        justifyContent: collapsed ? "center" : "flex-start",
                        gap: collapsed ? 0 : 8,
                        background: (location.pathname === to || location.pathname.startsWith(to + "/")) ? "#ffffff" : "transparent",
                        color: (location.pathname === to || location.pathname.startsWith(to + "/")) ? uiTheme.primaryDark : undefined,
                        boxShadow: (location.pathname === to || location.pathname.startsWith(to + "/")) ? "0 6px 16px rgba(0,0,0,.12)" : "none",
                      }}
                      title={collapsed ? label : ""}
                      onMouseEnter={(e) => { if (!(location.pathname === to || location.pathname.startsWith(to + "/"))) e.currentTarget.style.background = uiTheme.sidebarHover; }}
                      onMouseLeave={(e) => { if (!(location.pathname === to || location.pathname.startsWith(to + "/"))) e.currentTarget.style.background = "transparent"; }}
                    >
                      <i
                        className={`bi ${icon}`}
                        style={{
                          fontSize: 13,
                        }}
                      ></i>

                      {!collapsed && <span>{label}</span>}
                    </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
}

function Topbar() {
  const { user, logout, isAuthenticated } = useAuth();
  const { edition } = useEdition();
  const handleLogout = async () => {
    const takeBackup = window.confirm("Do you want to take a database backup before logout?");
    await logout({ takeBackup });
  };
  const { years, financialYearKey, financialYear, changeFinancialYear } =
    useFinancialYear();

  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const currentDate = now.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const currentTime = now.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  return (
    <div
      className="d-flex flex-wrap justify-content-between align-items-center gap-2"
      style={{
        padding: "7px 12px",
        marginBottom: 10,
        minHeight: 54,
        position: "sticky",
        top: 0,
        zIndex: 1000,
        borderRadius: 12,
        background: "rgba(255,255,255,.86)",
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(15,118,110,.10)",
        boxShadow: "0 8px 24px rgba(15,23,42,.08)",
      }}
    >
      <div>
        <div
          style={{
            fontSize: 10,
            color: "#6c757d",
          }}
        >
          Company Name:{" "}
        </div>
        <div>
          <span style={{ fontWeight: 700, color: "#212529" }}>
            {user?.companyName || (isAuthenticated ? "Setup required" : "First-run setup mode")}
          </span>{" "}
        </div>

        {/* <div
          style={{
            fontSize: 10,
            color: "#6c757d",
          }}
        >
          Company Name:{" "}
          <span style={{ fontWeight: 700, color: "#212529" }}>
            {user?.companyName || (isAuthenticated ? "Setup required" : "First-run setup mode")}
          </span>{" "}
          • Role:{" "}
          <span style={{ fontWeight: 700, color: "#212529" }}>
            {user?.roleName}
          </span>
        </div> */}
      </div>

      <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
        <span className="badge rounded-pill text-bg-dark" style={{ fontSize: 10 }}><i className="bi bi-layers me-1"></i>{edition.badge}</span>
        <ApiHealthBadge />
        {/* Date & Time */}
        <div
          className="d-flex align-items-center gap-2"
          style={{
            background: "#f8fafc",
            border: "1px solid #dee2e6",
            borderRadius: 8,
            padding: "5px 10px",
            minHeight: 32,
          }}
        >
          <i
            className="bi bi-clock-history text-primary"
            style={{ fontSize: 13 }}
          ></i>

          <div
            style={{
              fontSize: 10,
              lineHeight: 1.2,
            }}
          >
            <div className="fw-semibold">{currentDate}</div>
            <div style={{ color: "#0d6efd", fontWeight: 700 }}>
              {currentTime}
            </div>
          </div>
        </div>

        {/* Financial Year */}
        <div
          className="d-flex align-items-center gap-1"
          style={{
            background: "#f8fafc",
            border: "1px solid #dee2e6",
            borderRadius: 8,
            padding: "4px 8px",
          }}
        >
          <i className="bi bi-calendar3 text-primary"></i>

          <select
            className="form-select form-select-sm border-0 bg-transparent py-0"
            style={{
              fontSize: 11,
              width: 145,
              minHeight: 26,
              boxShadow: "none",
            }}
            value={financialYearKey}
            onChange={(e) => changeFinancialYear(e.target.value)}
          >
            {years.map((y) => (
              <option key={y.key} value={y.key}>
                {y.label}
              </option>
            ))}
          </select>

          <span
            className="badge"
            style={{
              fontSize: 9,
              padding: "5px 7px",
              background: `linear-gradient(90deg,${uiTheme.primaryDark},${uiTheme.primary})`,
              color: "#fff",
            }}
          >
            {financialYear.fromDate} - {financialYear.toDate}
          </span>
        </div>

        {/* User */}
        {isAuthenticated && <div
          className="d-flex align-items-center gap-2"
          style={{
            background: "#f8fafc",
            borderRadius: 8,
            padding: "4px 8px",
          }}
        >
          <div
            className="rounded-circle text-white d-flex align-items-center justify-content-center"
            style={{
              width: 28,
              height: 28,
              fontSize: 11,
              fontWeight: 700,
              background: `linear-gradient(135deg,${uiTheme.primary},${uiTheme.accent})`,
            }}
          >
            {user?.fullName?.charAt(0)?.toUpperCase() || "U"}
          </div>

          <div style={{ lineHeight: 1.1 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {user?.fullName}
            </div>

            <div
              style={{
                fontSize: 9,
                color: "#6c757d",
              }}
            >
              {user?.roleName}
            </div>
          </div>
        </div>}

        {/* Logout */}
        {isAuthenticated && <button
          className="btn btn-outline-danger btn-sm"
          style={{
            fontSize: 11,
            borderRadius: 8,
            padding: "5px 10px",
            fontWeight: 600,
          }}
          onClick={handleLogout}
        >
          <i className="bi bi-box-arrow-right me-1"></i>
          Logout
        </button>}
      </div>
    </div>
  );
}

function Shell({ children }) {
  useKeyboardShortcuts();
  return (
    <div
      className="d-flex"
      style={{
        height: "100vh",
        overflow: "hidden",
        background: `radial-gradient(circle at top left, rgba(15,118,110,.12), transparent 34%), ${uiTheme.pageBg}`,
      }}
    >
      <Sidebar />
      <main
        className="flex-grow-1"
        style={{
          width: 0,
          minWidth: 0,
          height: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          padding: 12,
        }}
      >
        <div className="container-fluid">
          <Topbar />
          <WorkspaceTabs />
          <div className="quanta-page-surface">{children}</div>
        </div>
      </main>
    </div>
  );
}

const routeList = [
  ["/dashboard", <CompanyReportsDashboardPage />, "REPORT_VIEW"],
  ["/accounts", <AccountsPage />, "ACCOUNT_VIEW"],
  ["/journal", <JournalPage />, "JOURNAL_SAVE"],
  ["/edit-manager", <EditManagerPage />, "JOURNAL_SAVE"],
  ["/ledger", <LedgerPage />, "REPORT_VIEW"],
  ["/trial-balance", <TrialBalancePage />, "REPORT_VIEW"],
  ["/profit-loss", <ProfitLossPage />, "REPORT_VIEW"],
  ["/balance-sheet", <BalanceSheetPage />, "REPORT_VIEW"],
  ["/final-accounts", <FinalAccountsPage />, "REPORT_VIEW"],
  ["/year-closing", <YearClosingPage />, "YEAR_CLOSE"],
  ["/items", <ItemsPage />, "ITEM_VIEW"],
  ["/stock-ledger", <StockLedgerPage />, "STOCK_LEDGER_VIEW"],
  ["/stock-summary", <StockSummaryPage />, "STOCK_LEDGER_VIEW"],
  ["/stock-adjustment", <StockAdjustmentPage />, "STOCK_ADJUST"],
  ["/employees", <EmployeesPage />, "USER_SAVE"],
  ["/payroll", <PayrollPage />, "USER_SAVE"],
  ["/customers", <CustomersPage />, "SALES_SAVE"],
  ["/suppliers", <SuppliersPage />, "PURCHASE_SAVE"],
  ["/invoices", <InvoicesPage />, "SALES_SAVE"],
  ["/sales-voucher", <SalesVoucherPage />, "SALES_SAVE"],
  ["/sales-return", <SalesReturnPage />, "SALES_SAVE"],
  ["/purchase-voucher", <PurchaseVoucherPage />, "PURCHASE_SAVE"],
  ["/credit-note", <CreditNotePage />, "SALES_SAVE"],
  ["/debit-note", <DebitNotePage />, "PURCHASE_SAVE"],
  ["/receipt-voucher", <ReceiptVoucherPage />, "JOURNAL_SAVE"],
  ["/payment-voucher", <PaymentVoucherPage />, "JOURNAL_SAVE"],
  ["/invoice-print/:id", <InvoicePrintPage />, "SALES_SAVE"],
  ["/invoice-templates", <InvoiceTemplateListPage />, "SALES_SAVE"],
  [
    "/invoice-templates/designer",
    <InvoiceTemplateDesignerPage />,
    "SALES_SAVE",
  ],
  [
    "/invoice-templates/designer/:id",
    <InvoiceTemplateDesignerPage />,
    "SALES_SAVE",
  ],
  ["/invoice-templates/print/:id", <InvoiceTemplatePrintPage />, "SALES_SAVE"],
  ["/gst-reports", <GstReportsPage />, "GST_REPORT_VIEW"],
  ["/gst-final-reports", <GstFinalReportsPage />, "GST_REPORT_VIEW"],

  ["/gst-enhancements", <GstEnhancementsPage />, "GST_REPORT_VIEW"],
  ["/eway-bill", <EWayBillPage />, "GST_REPORT_VIEW"],
  ["/cash-book", <CashBookPage />, "REPORT_VIEW"],
  ["/contra-voucher", <ContraVoucherPage />, "JOURNAL_SAVE"],
  ["/day-book", <DayBookPage />, "REPORT_VIEW"],
  ["/customer-outstanding", <CustomerOutstandingPage />, "REPORT_VIEW"],
  ["/supplier-outstanding", <SupplierOutstandingPage />, "REPORT_VIEW"],
  ["/bank-reconciliation", <BankReconciliationPage />, "REPORT_VIEW"],
  ["/opening-balance", <OpeningBalancePage />, "JOURNAL_SAVE"],
  ["/bank-book", <BankBookPage />, "REPORT_VIEW"],
  ["/companies", <CompanySetupPage />, "COMPANY_VIEW"],
  ["/admin-users", <AdminUsersPage />, "USER_SAVE"],
  ["/permissions", <PermissionsPage />, "PERMISSION_SAVE"],
  ["/license", <LicensePage />, "LICENSE_GENERATE"],
  ["/audit-logs", <AuditLogsPage />, "AUDIT_VIEW"],
  ["/transaction-logs", <TransactionLogsPage />, "AUDIT_VIEW"],
  ["/deleted-entries", <DeletedEntriesPage />, "AUDIT_VIEW"],
  ["/user-activity", <UserActivityPage />, "AUDIT_VIEW"],
  ["/system-settings", <SystemSettingsPage />, "PERMISSION_SAVE"],
  ["/edition-settings", <EditionSettingsPage />, "PERMISSION_SAVE"],
  ["/gst-voucher", <GstVoucherPage />, "GST_VOUCHER_SAVE"],
  ["/unauthorized", <UnauthorizedPage />, null],
];

function StartupGate({ children }) {
  const [status, setStatus] = useState({ loading: true, message: "Checking setup status..." });
  const location = useLocation();

  useEffect(() => {
    let active = true;
    const check = async () => {
      try {
        const res = await import("./api/client").then((m) =>
          m.default.get("/setup/startup-status", {
            skipGlobalLoading: true,
            skipAuthRedirect: true,
          }),
        );
        if (!active) return;
        setStatus({ loading: false, ...(res.data || {}) });
      } catch (e) {
        if (!active) return;
        setStatus({
          loading: false,
          databaseExists: false,
          companyExists: false,
          loginReady: false,
          nextPage: "system-settings",
          message: e.userMessage || "API is not reachable. Configure API/SQL Server from System Settings.",
        });
      }
    };
    check();
    return () => { active = false; };
  }, []);

  if (status.loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="card border-0 shadow-sm p-4 text-center" style={{ borderRadius: 14 }}>
          <div className="spinner-border text-primary mx-auto mb-3" role="status" />
          <div className="fw-bold">Checking ERP setup...</div>
          <div className="text-muted small">Please wait while database and company setup are verified.</div>
        </div>
      </div>
    );
  }

  const path = location.pathname.toLowerCase();
  if (!status.databaseExists && path !== "/system-settings") {
    return <Navigate to="/system-settings" replace state={{ setupMessage: status.message }} />;
  }
  if (status.databaseExists && !status.companyExists && path !== "/company-setup" && path !== "/system-settings") {
    return <Navigate to="/company-setup" replace state={{ setupMessage: status.message }} />;
  }
  const hasToken = !!localStorage.getItem("token");
  if (status.databaseExists && status.companyExists && path === "/") {
    return <Navigate to="/login" replace />;
  }
  if (status.databaseExists && status.companyExists && !hasToken && ["/system-settings", "/company-setup"].includes(path)) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function AppRoutes() {
  const { canUseRoute } = useEdition();
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/system-settings" element={<Shell><ErrorBoundary><SystemSettingsPage /></ErrorBoundary></Shell>} />
      <Route path="/company-setup" element={<Shell><ErrorBoundary><CompanySetupPage /></ErrorBoundary></Shell>} />
      {routeList.map(([path, element, permission]) => (
        <Route
          key={path}
          path={path}
          element={
            <ProtectedRoute permission={permission}>
              <Shell><ErrorBoundary>{canUseRoute(path) ? element : <UnauthorizedPage />}</ErrorBoundary></Shell>
            </ProtectedRoute>
          }
        />
      ))}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <EditionProvider>
          <FinancialYearProvider>
            <StartupGate>
              <AppRoutes />
            </StartupGate>
            <GlobalLoadingOverlay />
          </FinancialYearProvider>
        </EditionProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
